import React, { useMemo, useRef, useState } from 'react';
import { Tooltip } from 'antd';
import { RightOutlined } from '@ant-design/icons';
import { ListBase } from 'react-admin';
import { NetOrcaTableListV1, TableListColumn } from '../NetOrcaTableList';
import { getTagForAIProcessResponse } from '../Tags';
import JsonViewer from '../JsonViewer';
import CollapsePanel from '../CollapsePanel';
import { useExpandedRow } from '../OpenCard';
import { formatDateAndTime, formatDateAndTimeFromNow } from '../../../helpers/utils';
import { ResponsesConfig } from './types';
import './ResponsesTable.css';

const PER_PAGE = 25;

const codeTone = (statusCode?: number, state?: string) => {
    if (!statusCode) return state === 'FAILED' ? 'bad' : 'warn';
    if (statusCode >= 200 && statusCode < 300) return 'good';
    if (statusCode >= 400) return 'bad';
    return 'warn';
};

/** ExternalAPIStateMixin.States, for the responses with no status code yet */
const codeLabel = (statusCode?: number, state?: string) => {
    if (statusCode) return String(statusCode);
    if (state === 'SCHEDULED') return 'queued';
    if (state === 'WAITING_FOR_RESPONSE') return 'sent';
    return '—';
};

/** the states the API records, offered as the State column's filter */
export const RESPONSE_STATE_OPTIONS = [
    { value: 'OK', label: 'OK' },
    { value: 'FAILED', label: 'Failed' },
    { value: 'SCHEDULED', label: 'Scheduled' },
    { value: 'WAITING_FOR_RESPONSE', label: 'Waiting for response' },
];

/**
 * One response under the shared column header. The row opens onto the body the
 * endpoint sent back, which is the only thing here worth more than a line —
 * by the rules every row that opens in place follows (useExpandedRow).
 */
const ResponseRow = ({
    record,
    config,
    open,
    toggle,
}: {
    record: any;
    config: ResponsesConfig;
    open: boolean;
    toggle: () => void;
}) => {
    const ref = useRef<HTMLDivElement>(null);
    const headRef = useRef<HTMLDivElement>(null);
    useExpandedRow(ref, headRef, open);

    return (
        <div ref={ref} className={`nres-row${open ? ' nres-row--open' : ''}`}>
            <div
                ref={headRef}
                className="nres-row__head"
                role="button"
                tabIndex={0}
                aria-expanded={open}
                onClick={toggle}
                onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        toggle();
                    }
                }}
            >
                <span className={`nres-code nres-code--${codeTone(record.status_code, record.state)}`}>
                    <span className="nres-sr">Status </span>
                    {codeLabel(record.status_code, record.state)}
                </span>

                {(config.columns ?? []).map((column) => {
                    const value = column.value(record);
                    return (
                        <Tooltip
                            key={column.label}
                            placement="topLeft"
                            title={typeof value === 'string' ? value : undefined}
                        >
                            <span className="nres-cell" data-label={column.label}>
                                {value || '—'}
                            </span>
                        </Tooltip>
                    );
                })}

                <span className="nres-cell nres-cell--state" data-label="State">
                    {record.state ? getTagForAIProcessResponse(record.state) : '—'}
                </span>

                <Tooltip title={record.created ? formatDateAndTime(record.created) : undefined}>
                    <span className="nres-cell nres-cell--time" data-label="Triggered">
                        {record.created ? formatDateAndTimeFromNow(record.created) : '—'}
                    </span>
                </Tooltip>

                <RightOutlined className="nres-chev" aria-hidden="true" />
            </div>

            <CollapsePanel open={open} className="nres-row__body nx-open-row__panel">
                {/* same call the Pack pipeline raw-JSON tabs make */}
                {record.body ? (
                    <JsonViewer data={record.body} fontSize="13px" lineHeight="1.6" />
                ) : (
                    <span className="nres-empty-body">No response body was recorded.</span>
                )}
            </CollapsePanel>
        </div>
    );
};

const ResponsesTableView = ({ config }: { config: ResponsesConfig }) => {
    // one body open at a time: two of them push everything else off the screen
    const [openId, setOpenId] = useState<number | null>(null);

    const identity = config.columns ?? [];

    const columns: TableListColumn[] = useMemo(() => [
        // the code is what the reader scans for, so it leads and never wraps
        { label: 'Status' },
        ...identity.map((column) => ({ label: column.label, filters: column.filters })),
        { label: 'State', filters: ['state'] },
        { label: 'Triggered' },
        // the last track is the chevron's, which the header cannot name
    ], [identity]);

    return (
        <NetOrcaTableListV1
            // the column count decides the track template, so it names itself
            className={`netorca-nres netorca-nres--${identity.length}`}
            dataCy="response-result-list"
            fields={config.filters ?? []}
            columns={columns}
            template="var(--nres-cols)"
            emptyText={config.emptyText}
            noun="response"
            renderRow={(record) => (
                <ResponseRow
                    key={record.id}
                    record={record}
                    config={config}
                    open={openId === record.id}
                    toggle={() => setOpenId((prev) => (prev === record.id ? null : record.id))}
                />
            )}
        />
    );
};

/**
 * The response log for an integration, newest first, in the standard list. The
 * responses are their own resource under their own filters, so they bring their
 * own ListBase rather than borrowing the page's.
 *
 * `filter` narrows the set to one thing — one service item's responses, say —
 * and is the page's, not the reader's: it is not offered as a control and
 * cannot be cleared.
 */
const ResponsesTable = ({ config, filter }: { config: ResponsesConfig; filter?: Record<string, any> }) => (
    <ListBase
        resource={config.resource}
        basePath={`/${config.resource}`}
        // page size is the batch the infinite scroll fetches
        perPage={PER_PAGE}
        sort={{ field: 'created', order: 'DESC' }}
        filter={filter}
    >
        <ResponsesTableView config={config} />
    </ListBase>
);

export default ResponsesTable;
